'use client';

import { isJwtExpiredEdge } from '@/lib/auth/jwt-expiry';

/**
 * WX JWT client-side storage management for Hire Go.
 *
 * This module manages the WX JWT in browser localStorage so the WX Copilot
 * UMD widget can authenticate its requests to WX services and Fountain AI.
 *
 * Runtime: browser only ('use client'). For server-side JWT parsing and
 * validation, see lib/auth/wx-jwt.ts.
 */

const BASE_KEY = 'token_employer';

function getStage(): string {
  return process.env.NEXT_PUBLIC_STAGE ?? 'staging';
}

function getStageKey(): string {
  return `${BASE_KEY}_${getStage()}`;
}

export function storeWxJwtInLocalStorage(jwt: string): void {
  if (!jwt) {
    return;
  }
  const stageKey = getStageKey();

  try {
    localStorage.setItem(stageKey, jwt);
  } catch (e) {
    console.warn('Failed to store WX JWT in localStorage', e);
  }
}

export function clearWxJwt(): void {
  const stageKey = getStageKey();

  try {
    localStorage.removeItem(stageKey);
  } catch (e) {
    console.warn('Failed to remove WX JWT from localStorage', e);
  }
}

export function getWxJwt(): string | null {
  const stageKey = getStageKey();

  try {
    return localStorage.getItem(stageKey);
  } catch (e) {
    console.warn('Failed to get WX JWT from storage', e);
    return null;
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch (e) {
    console.warn('Failed to decode JWT:', e);
    return null;
  }
}

// Checks if the WX token isn't expired
// DOES NOT YET CHECK IF THE TOKEN IS VALID OR SIGNED CORRECTLY
export function isWxJwtValid(): boolean {
  const token = getWxJwt();
  if (!token) {
    return false;
  }

  if (isJwtExpiredEdge(token)) {
    clearWxJwt();
    return false;
  }

  return true;
}

/**
 * Get user context from the WX JWT token
 *
 * WX JWTs have a nested structure:
 * - user.uuid: WX user UUID
 * - user.companyUuid: WX company UUID
 * - hireAccountId: Hire account UUID
 * - user.email: User email
 */
export function getWxUserContext(): Record<string, unknown> | null {
  const token = getWxJwt();
  if (!token) {
    return null;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const user = (payload.user as Record<string, unknown>) ?? {};

  return {
    wxUserId: user.uuid ?? null,
    wxCompanyId: user.companyUuid ?? null,
    hireAccountId: payload.hireAccountId ?? null,
    email: user.email ?? payload.email ?? null,
    audience: user.audience ?? payload.aud ?? null,
    audienceUuid: user.audienceUuid ?? null,
    user,
  };
}
